import React, {useCallback, useEffect, useMemo, useState} from "react";
import NiceModal from "@ebay/nice-modal-react";
import {useSearchParams} from "react-router-dom";
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {restrictToVerticalAxis} from "@dnd-kit/modifiers";
import UndoIcon from "@mui/icons-material/Undo";
import {
  SortableContext,
  SortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {GridColDef} from "@mui/x-data-grid";
import {toast} from "sonner";
import {Box, Chip, Colors, Container, Text} from "@bounce/shared/ui";
import {Button, ListDataGrid, ListPageHeader, RowActions, SearchInput} from "../../ui/components";
import DeleteSegmentDialog from "./DeleteSegmentDialog";
import {useGetSegments} from "../../apis/requests/segments/getSegments";
import {useSaveSegmentsBatch} from "../../apis/requests/segments/saveSegmentsBatch";
import {ReachSegment, ReachSegmentCreatePayload, SegmentCategory} from "../../apis/requests/segments/types";
import SegmentDialog from "./SegmentDialog";
import {SEGMENT_QUERY_PARAM} from "./segmentLinks";
import {SEGMENT_TYPE_INFO} from "./segment-form/constants";
import {segmentCategoryOf} from "./segment-form/helpers";
import {DndEnabledContext, DragHandleCell} from "../../components/sortable";
import {SegmentTypeSeparatorContext} from "./SegmentDndContext";
import {SEGMENT_TYPE_SEPARATOR_HEIGHT} from "./SegmentTypeSeparator";
import {SortableSegmentRow} from "./SortableSegmentRow";
import {StagedSegment, StagedStatus, useStagedSegments} from "./useStagedSegments";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 100;
const FIRST_GENERAL_ROW_CLASS_NAME = "first-general-segment-row";
const DELETED_ROW_CLASS_NAME = "deleted-segment-row";

const STAGED_STATUS_CHIP: Record<StagedStatus, {label: string; variant: "success" | "warning" | "error"}> = {
  new: {label: "New", variant: "success"},
  edited: {label: "Edited", variant: "warning"},
  deleted: {label: "Deleted", variant: "error"},
};

const isExclusive = (segment: ReachSegment | undefined) => segment?.type === "EXCLUSIVE";

const PAGE_TITLE_BY_CATEGORY: Record<SegmentCategory, string> = {
  marketing: "Marketing segments",
  validation: "Validation segments",
};

type Props = {
  category: SegmentCategory;
};

const SegmentsPage = ({category}: Props) => {
  const {data, isLoading, error} = useGetSegments();
  const {mutate: saveSegmentsBatch, isPending: isSaving} = useSaveSegmentsBatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedSegmentId = Number(searchParams.get(SEGMENT_QUERY_PARAM));
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: DEFAULT_PAGE_SIZE});

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 8}}),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  );

  const serverSorted = useMemo(
    () => (data ?? []).filter((s) => segmentCategoryOf(s) === category).sort((a, b) => a.priority - b.priority),
    [data, category]
  );
  const {
    segments: orderedSegments,
    changeCount,
    stageCreate,
    stageUpdate,
    stageDelete,
    stageOrder,
    undo,
    discardAll,
    buildBatchPayload,
  } = useStagedSegments(serverSorted);
  const hasChanges = changeCount > 0;
  const segmentById = useMemo(() => new Map(orderedSegments.map((s) => [s.id, s])), [orderedSegments]);

  const filteredSegments = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orderedSegments;
    return orderedSegments.filter(
      (s) => s.name.toLowerCase().includes(q) || (s.description ?? "").toLowerCase().includes(q)
    );
  }, [orderedSegments, search]);

  const rankById = useMemo(
    () => new Map(filteredSegments.filter((s) => s.stagedStatus !== "deleted").map((s, i) => [s.id, i + 1] as const)),
    [filteredSegments]
  );

  const firstGeneralId = useMemo(() => {
    if (!filteredSegments.some(isExclusive)) return undefined;
    return filteredSegments.find((s) => !isExclusive(s))?.id;
  }, [filteredSegments]);

  const [isCrossTypeDrag, setIsCrossTypeDrag] = useState(false);

  const pageRowIds = useMemo(() => {
    const {page, pageSize} = paginationModel;
    return filteredSegments.slice(page * pageSize, (page + 1) * pageSize).map((s) => s.id);
  }, [filteredSegments, paginationModel]);

  const isSameType = useCallback(
    (firstId: number | string, secondId: number | string) =>
      isExclusive(segmentById.get(Number(firstId))) === isExclusive(segmentById.get(Number(secondId))),
    [segmentById]
  );

  const sameTypeSortingStrategy = useMemo<SortingStrategy>(
    () => (args) => {
      const activeId = pageRowIds[args.activeIndex];
      const overId = pageRowIds[args.overIndex];
      if (activeId === undefined || overId === undefined || !isSameType(activeId, overId)) return null;
      return verticalListSortingStrategy(args);
    },
    [pageRowIds, isSameType]
  );

  const handleDragOver = useCallback(
    ({active, over}: DragOverEvent) => setIsCrossTypeDrag(Boolean(over) && !isSameType(active.id, over!.id)),
    [isSameType]
  );

  const isSearching = search.trim().length > 0;
  const dndEnabled = !isSearching;

  const onDelete = (segment: StagedSegment) => {
    if (segment.stagedStatus === "new") {
      stageDelete(segment.id);
      return;
    }
    NiceModal.show(DeleteSegmentDialog, {
      segmentId: String(segment.id),
      segmentName: segment.name,
      onConfirm: () => stageDelete(segment.id),
    });
  };

  const openSegment = (segment: StagedSegment) => {
    if (segment.stagedStatus === "deleted") return;
    NiceModal.show(SegmentDialog, {
      segment,
      category,
      onStage: (payload: ReachSegmentCreatePayload) => stageUpdate(segment.id, payload),
    });
  };

  useEffect(() => {
    const linkedSegment = segmentById.get(linkedSegmentId);
    if (!linkedSegment) return;
    setSearchParams(
      (params) => {
        params.delete(SEGMENT_QUERY_PARAM);
        return params;
      },
      {replace: true}
    );
    openSegment(linkedSegment);
  }, [linkedSegmentId, segmentById, setSearchParams, openSegment]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const {active, over} = event;
      setIsCrossTypeDrag(false);
      if (!over || active.id === over.id || !isSameType(active.id, over.id)) return;
      const orderedIds = orderedSegments.map((s) => s.id);
      const oldIndex = orderedIds.indexOf(Number(active.id));
      const newIndex = orderedIds.indexOf(Number(over.id));
      stageOrder(arrayMove(orderedIds, oldIndex, newIndex));
    },
    [orderedSegments, isSameType, stageOrder]
  );

  const handleSaveAll = () => {
    saveSegmentsBatch(buildBatchPayload(), {
      onSuccess: () => {
        toast.success(
          category === "marketing" ? "Segments saved. Audiences are being recalculated." : "Segments saved"
        );
        discardAll();
      },
      onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "Failed to save segments"),
    });
  };

  const columns = useMemo<GridColDef<StagedSegment>[]>(
    () => [
      {
        field: "__drag__",
        headerName: "",
        width: 48,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: () => <DragHandleCell />,
      },
      {
        field: "priority",
        headerName: "#",
        width: 70,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({row}) => (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              backgroundColor: isExclusive(row) ? Colors.blue._600 : Colors.tertiary._100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Text
              size={12}
              variant="inter600"
              sx={{color: isExclusive(row) ? Colors.bw.white : Colors.grey._700, textAlign: "center"}}
            >
              {rankById.get(row.id) ?? "–"}
            </Text>
          </Box>
        ),
      },
      {
        field: "name",
        headerName: "Segment name",
        flex: 2,
        renderCell: ({row}) => (
          <Box sx={{minWidth: 0}}>
            <Container row sx={{gap: 1, alignItems: "center", justifyContent: "flex-start"}}>
              <Text
                size={14}
                variant="inter600"
                sx={{color: Colors.grey._900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
              >
                {row.name}
              </Text>
              {row.stagedStatus && (
                <Chip
                  size_="small"
                  variant_={STAGED_STATUS_CHIP[row.stagedStatus].variant}
                  label={STAGED_STATUS_CHIP[row.stagedStatus].label}
                />
              )}
            </Container>
            {row.description && (
              <Text
                size={13}
                sx={{color: Colors.grey._500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
              >
                {row.description}
              </Text>
            )}
          </Box>
        ),
      },
      ...(category === "marketing"
        ? [
            {
              field: "type",
              headerName: "Type",
              width: 110,
              sortable: false,
              renderCell: ({row}) => (
                <Chip
                  size_="small"
                  variant_={isExclusive(row) ? "info" : "inherit"}
                  label={SEGMENT_TYPE_INFO[row.type].label}
                />
              ),
            } satisfies GridColDef<StagedSegment>,
          ]
        : []),
      {
        field: "conditions",
        headerName: "Conditions",
        flex: 2,
        sortable: false,
        renderCell: ({row}) => (
          <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", py: 1}}>
            {row.conditions.rules.map((rule, i) => (
              <Chip
                key={i}
                size_="small"
                variant_="blue"
                label={"rules" in rule ? rule.name || `Group ${i + 1}` : `Group ${i + 1}`}
              />
            ))}
          </Box>
        ),
      },
      {
        field: "matching",
        headerName: "Matching",
        flex: 1,
        sortable: false,
        renderCell: ({row}) => {
          const matchMode = row.conditions.match_mode.charAt(0).toUpperCase() + row.conditions.match_mode.slice(1);
          return <Chip size_="small" variant_="inherit" label={`${matchMode}`} />;
        },
      },
      {
        field: "principal",
        headerName: "Created by",
        flex: 1,
        renderCell: ({row}) => (
          <Text
            size={14}
            sx={{color: Colors.grey._700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
          >
            {row.principal ? row.principal.split("@")[0] : ""}
          </Text>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 72,
        sortable: false,
        renderCell: ({row}) => {
          const undoAction = row.stagedStatus
            ? [{label: "Undo changes", icon: <UndoIcon sx={{width: 20, height: 20}} />, onClick: () => undo(row.id)}]
            : [];
          if (row.stagedStatus === "deleted") return <RowActions extraActions={undoAction} />;
          return (
            <RowActions onEdit={() => openSegment(row)} onDelete={() => onDelete(row)} extraActions={undoAction} />
          );
        },
      },
    ],
    [rankById, category, undo, stageDelete, stageUpdate]
  );

  return (
    <Box sx={{p: 4, display: "flex", flexDirection: "column", height: "100%"}}>
      <ListPageHeader title={PAGE_TITLE_BY_CATEGORY[category]}>
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPaginationModel((m) => ({...m, page: 0}));
          }}
          placeholder="Search segments..."
        />
        <Button
          buttonType="contained-primary"
          size="small"
          onClick={() => NiceModal.show(SegmentDialog, {category, onStage: stageCreate})}
        >
          + Add segment
        </Button>
      </ListPageHeader>
      {hasChanges && (
        <Container
          row
          fullWidth
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.5,
            py: 1.5,
            mb: 2,
            borderRadius: "12px",
            backgroundColor: Colors.blue._100,
          }}
        >
          <Box>
            <Text size={13} variant="inter600" sx={{color: Colors.blue._800}}>
              {changeCount} unsaved change{changeCount > 1 ? "s" : ""}
            </Text>
            {category === "marketing" && (
              <Text size={12} sx={{color: Colors.blue._800}}>
                Saving will recalculate audiences once
              </Text>
            )}
          </Box>
          <Container row sx={{gap: 1}}>
            <Button
              buttonType="outlined-primary"
              size="small"
              onClick={discardAll}
              disabled={isSaving}
              sx={{
                borderColor: Colors.blue._300,
                color: Colors.blue._800,
                "&:hover": {borderColor: Colors.blue._500, backgroundColor: Colors.blue._200},
              }}
            >
              Discard all
            </Button>
            <Button buttonType="contained-primary" size="small" onClick={handleSaveAll} disabled={isSaving}>
              {isSaving ? "Saving…" : "Save all"}
            </Button>
          </Container>
        </Container>
      )}
      {error ? (
        <Text size={14}>Failed to load segments.</Text>
      ) : (
        <DndEnabledContext.Provider value={dndEnabled}>
          <SegmentTypeSeparatorContext.Provider value={{firstGeneralId, isBlocked: isCrossTypeDrag}}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={() => setIsCrossTypeDrag(false)}
            >
              <SortableContext items={pageRowIds} strategy={sameTypeSortingStrategy}>
                <ListDataGrid
                  rows={filteredSegments}
                  columns={columns}
                  loading={isLoading}
                  getRowId={(row) => row.id}
                  getRowClassName={({row}) =>
                    [
                      row.id === firstGeneralId ? FIRST_GENERAL_ROW_CLASS_NAME : "",
                      row.stagedStatus === "deleted" ? DELETED_ROW_CLASS_NAME : "",
                    ].join(" ")
                  }
                  getRowSpacing={({id}) => ({top: id === firstGeneralId ? SEGMENT_TYPE_SEPARATOR_HEIGHT : 0})}
                  sx={{
                    [`& .MuiDataGrid-row.${FIRST_GENERAL_ROW_CLASS_NAME}`]: {
                      borderBottom: `1px solid ${Colors.tertiary._200}`,
                    },
                    [`& .MuiDataGrid-row.${DELETED_ROW_CLASS_NAME}`]: {
                      opacity: 0.5,
                      textDecoration: "line-through",
                    },
                  }}
                  onRowClick={(params) => openSegment(params.row)}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  slots={{row: SortableSegmentRow}}
                  noRowsText={isSearching ? "No segments match your search" : "No segments yet"}
                />
              </SortableContext>
            </DndContext>
          </SegmentTypeSeparatorContext.Provider>
        </DndEnabledContext.Provider>
      )}
    </Box>
  );
};

export default SegmentsPage;
